/** Attachments: local previews now, upload to the artifact origin on send. */

import { type Api, type Fetcher, putArtifact } from './api.js'
import type { BrowserArtifacts, EncryptedArtifactRef } from './encrypted-artifacts.js'

export interface PendingFile {
  id: string
  file: File
  name: string
  url: string
}

export function toPending(files: Iterable<File>): PendingFile[] {
  return [...files].map((file) => ({
    id: crypto.randomUUID(),
    file,
    name: file.name || 'pasted image',
    url: URL.createObjectURL(file),
  }))
}

export function imagesFromClipboard(e: ClipboardEvent | React.ClipboardEvent): File[] {
  const items = e.clipboardData?.items ?? []
  const out: File[] = []
  for (const it of Array.from(items)) {
    if (it.kind === 'file' && it.type.startsWith('image/')) {
      const f = it.getAsFile()
      if (f) out.push(f)
    }
  }
  return out
}

const encryptedUploads = new WeakMap<
  BrowserArtifacts,
  WeakMap<File, Map<string, Awaited<ReturnType<BrowserArtifacts['prepare']>>>>
>()

export async function uploadAll(
  api: Api,
  fetcher: Fetcher,
  files: PendingFile[],
  runId?: string,
  encrypted?: BrowserArtifacts,
) {
  const refs: {
    artifactId: string
    name: string
    mime: string
    encrypted?: EncryptedArtifactRef
  }[] = []
  for (const f of files) {
    if (encrypted) {
      if (!runId) throw new Error('encrypted attachment requires routing namespace')
      let cache = encryptedUploads.get(encrypted)
      if (!cache) {
        cache = new WeakMap()
        encryptedUploads.set(encrypted, cache)
      }
      let pending = cache.get(f.file)
      if (!pending) {
        pending = new Map()
        cache.set(f.file, pending)
      }
      let prepared = pending.get(runId)
      if (!prepared) {
        prepared = await encrypted.prepare(f.file, runId)
        pending.set(runId, prepared)
      }
      const ref = await prepared.upload()
      refs.push({ artifactId: ref.objectId, name: f.name, mime: f.file.type, encrypted: ref })
      continue
    }
    const ticket = await api.artifactUpload({
      name: f.name,
      mime: f.file.type || 'application/octet-stream',
      bytes: f.file.size,
      ...(runId ? { runId } : {}),
    })
    await putArtifact(fetcher, ticket.uploadUrl, f.file)
    refs.push({ artifactId: ticket.artifactId, name: f.name, mime: f.file.type })
  }
  return refs
}

export function revokeAll(files: PendingFile[]) {
  for (const f of files) URL.revokeObjectURL(f.url)
}
